/**
 * SG — Incorporation (Post) chatflow (B32110).
 * Title → L_ENTITY select → confirm + live document package (Entity autofill).
 * Field ✨ → officer-list (Director / Secretary / Shareholder / Contact) or entity reselect.
 */
import type { ConversationMessage, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

function resolveEntityCd(ctx: Record<string, any>, context: ChatFlowContext): string {
  return String(
    ctx.entityCd ||
      ctx.icpCd ||
      context.entityCd ||
      context.formData?.entityCd ||
      context.formData?.icpCd ||
      '',
  ).trim();
}

export const incorporationPostFlow: ChatFlow = {
  id: 'sg-incorporation-post',
  taskCodeMatch: ['B32110'],
  categoryMatch: [
    'incorporation (post)',
    'incorporation post',
    'incorporationpost',
    'post-incorporation',
    'post incorporation',
    'sg incorporation (post)',
    'sg incorporation post',
  ],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `Welcome to **SG Incorporation (Post)**.\n\n` +
          `This preset prepares the post-incorporation package` +
          (docCount > 0 ? ` (${docCount} template(s) linked)` : '') +
          `: Welcome Letter, First Directors' Resolution, Form A1, Share Applications, ` +
          `CSP agreements, Nomination / Indemnity, DDF, and Financial Year Checklist.\n\n` +
          `Select an **existing Entity** (L_ENTITY) — company, directors, secretary and members ` +
          `will auto-fill. Then complete remaining fields on the right.\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: {
        targetVarKey: '_taskTitle',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **Singapore company entity** for this post-incorporation package.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        analysis: {
          success: true,
          placeholder: '_entity',
          sourceResolver: 'ENTITY',
          officerRole: null,
          varKey: '_entity',
          varLabel: 'Entity',
          confidence: 1,
          message: 'Select Entity (SG Incorporation Post).',
          relatedFields: [],
          options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Entity linked. Review auto-filled fields on the right, complete remaining blanks, ' +
        'add Share Application / DDF copies as needed, then click **Create Task**.',
      widget: null,
      nextStep: null,
    },
    {
      id: 'officer-role',
      aiMessage: 'Please select a role for the person you want to look up.',
      widget: 'officer-role-buttons',
      awaitEvent: true,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a person from the list.',
      widget: 'officer-list',
      awaitEvent: true,
    },
    {
      id: 'officer-detail',
      aiMessage: 'Person details loaded. The form has been pre-filled.',
      widget: 'officer-field-picker',
      awaitEvent: true,
    },
    {
      id: 'address-input',
      aiMessage: 'Enter the address below.',
      widget: 'address-input',
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (
      (eventName === 'value-submit' || eventName === 'text-input-submit') &&
      context.currentStepId === 'input-title'
    ) {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { _taskTitle: String(title).trim(), taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [
          { role: 'user', content: `Selected: **${entityName}**`, timestamp: Date.now(), stepId: 1 },
        ],
        formUpdates: {
          entityCd,
          entityName,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || ent.regNo || '',
          tlinkData: {
            entityCd,
            icpCd: entityCd,
            entityName,
            companyName: entityName,
            registrationNo: ent.registrationNo || ent.regNo || '',
            ...ent,
          },
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const key = String(ctx.key || '');
      const scopedKey = String(ctx.scopedKey || key || '').trim();
      const role = String(ctx.defaultRole || '').trim().toUpperCase();

      // Company name → re-select L_ENTITY
      if (
        ctx.lookupType === 'entity' ||
        ctx.lookupType === 'incorporation' ||
        key === 'company_name' ||
        key === 'entity_name'
      ) {
        return {
          messages: [
            {
              role: 'user',
              content: 'Select a **company entity**…',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          formUpdates: { _personLookupCtx: ctx, _companyTargetKey: scopedKey || key },
          nextStepId: 'select-entity',
        };
      }

      const entityCd = resolveEntityCd(ctx, context);
      if (!entityCd && role !== 'CONTACT') {
        return {
          messages: [
            {
              role: 'ai',
              content: 'Please select an entity first.',
              timestamp: Date.now(),
              stepId: 1,
              source: 'system',
            },
          ],
        };
      }

      const listEntityCd = role === 'CONTACT' ? '' : entityCd;
      const widgetData: Record<string, any> = {
        selectedRole: role || undefined,
        role: role || undefined,
        entityCd: listEntityCd,
      };
      if (ctx.memberEntityKind === 'corporate' || ctx.memberEntityKind === 'individual') {
        widgetData.memberEntityKind = ctx.memberEntityKind;
      }
      if (ctx.contactCategoryCd) {
        widgetData.contactCategoryCd = String(ctx.contactCategoryCd);
      }

      if (role) {
        return {
          messages: [
            {
              role: 'user',
              content: `Looking up **${role}** for **${key || 'field'}**…`,
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          formUpdates: {
            _personLookupCtx: ctx,
            _personTargetKey: key,
            _personFieldGroup: ctx.fieldGroup || 'sg-incorporation-post',
          },
          nextStepId: 'officer-list',
          nextStepWidgetData: widgetData,
        };
      }

      return {
        messages: [
          {
            role: 'user',
            content: `Looking up person for **${key || 'field'}**…`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          _personLookupCtx: ctx,
          _personTargetKey: key,
          _personFieldGroup: ctx.fieldGroup || 'sg-incorporation-post',
        },
        nextStepId: 'officer-role',
      };
    }

    if (eventName === 'request-address-input') {
      const key = String(payload?.key || payload || '').trim();
      return {
        messages: [
          {
            role: 'user',
            content: key ? `Enter address for **${key}**.` : 'Enter an address.',
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: { _addressTargetKey: key },
        nextStepId: 'address-input',
      };
    }

    if (eventName === 'officer-role-select') {
      const role = typeof payload === 'string' ? payload : payload?.role || payload?.value;
      if (!role) return {};
      const roleUp = String(role).toUpperCase();
      const entityCd =
        roleUp === 'CONTACT' ? '' : resolveEntityCd({}, context);
      const prevCtx = (context.formData as any)?._personLookupCtx || {};
      return {
        messages: [
          {
            role: 'user',
            content: `Role: **${role}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          _personLookupCtx: { ...prevCtx, defaultRole: roleUp },
        },
        nextStepId: 'officer-list',
        nextStepWidgetData: {
          selectedRole: roleUp,
          role: roleUp,
          entityCd,
        },
      };
    }

    if (
      (eventName === 'officer-select' || eventName === 'person-select') &&
      (context.currentStepId === 'officer-list' || !context.currentStepId)
    ) {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected person';
      return {
        messages: [
          {
            role: 'user',
            content: `Selected: **${officerName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-detail',
        formUpdates: { _selectedOfficer: payload },
      };
    }

    // Roles 뒤로가기 — 항상 role 선택으로 (confirm 으로 튕기지 않음)
    if (eventName === 'officer-list-back' && context.currentStepId === 'officer-list') {
      return {
        nextStepId: 'officer-role',
        messages: [
          {
            role: 'ai',
            content: 'Please select a role for the person you want to look up.',
            timestamp: Date.now(),
            stepId: 1,
            source: 'system',
          },
        ],
      };
    }

    if (eventName === 'officer-field-apply' || eventName === 'value-submit') {
      if (context.currentStepId === 'officer-detail' || context.currentStepId === 'address-input') {
        return {
          messages: [
            {
              role: 'user',
              content: 'Applied to the document.',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'confirm',
        };
      }
    }

    // address-input widget submit
    if (eventName === 'entity-field-select' && context.currentStepId === 'address-input') {
      const value = typeof payload === 'string' ? payload : payload?.value || payload?.address || '';
      if (!value) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Entered: **${value}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
